import { ref } from 'vue'

// A synthetic mouseenter/mouseleave dispatched onto an element fires every
// listener bound to that event on the element — not just the PrimeVue
// directive's own native listener it's aimed at. Marking it lets our own
// hover-tracking handlers below tell "the pointer actually moved" apart from
// "something redispatched this to drive the directive," so a focus-driven
// synthetic mouseenter can't be mistaken for real hover (and vice versa).
const SYNTHETIC_TOOLTIP_EVENT = '__isSyntheticTooltipEvent'

// PrimeVue's Tooltip directive binds either hover (mouseenter/mouseleave) or
// the `.focus` modifier's focus/blur — never both. To keep pointer-hover
// tooltips working while also supporting keyboard focus, dispatch the hover
// event the directive already listens for from the focus/blur handlers; both
// listeners are attached directly to the element (not relying on bubbling),
// so this reuses the directive's real show/hide path.
export const dispatchTooltipHoverEvent = (event: Event, type: 'mouseenter' | 'mouseleave'): void => {
  const target = event.currentTarget as HTMLElement | null
  const synthetic = new MouseEvent(type)
  Object.defineProperty(synthetic, SYNTHETIC_TOOLTIP_EVENT, { value: true })
  target?.dispatchEvent(synthetic)
}

type TooltipTrigger = HTMLElement & { $_ptooltipId?: string | null }

// The directive's own show/hide is a single boolean driven by whichever event
// (real or synthetic) fired last, with no notion of "two independent inputs."
// That leaves a WCAG 1.4.13 (Content on Hover or Focus) gap: a blur can hide the
// tooltip while the pointer is still over the trigger, and a real mouseleave can
// hide it while the trigger still has keyboard focus. This tracks hover and focus
// as separate booleans and only lets the tooltip hide once both have ended —
// re-showing it (a synthetic mouseenter) if one input ends while the other is
// still active, and suppressing the hide dispatch entirely when the other input
// is still active.
export function useTooltipHoverFocusSync() {
  const isHovering = ref(false)
  const isFocused = ref(false)
  let focusedTrigger: TooltipTrigger | null = null

  const isSynthetic = (event: Event): boolean =>
    Boolean((event as unknown as Record<string, unknown>)[SYNTHETIC_TOOLTIP_EVENT])

  // The directive's own Escape handler hides the tooltip independently of this
  // composable's isFocused/isHovering tracking, so isFocused can stay true after
  // Escape removes the popup. Checking the popup is actually still in the DOM
  // (not just "isFocused") is what lets a later real hover reopen it instead of
  // being swallowed as a no-op "already open via focus" duplicate.
  const isTooltipVisible = (trigger: TooltipTrigger | null): boolean =>
    Boolean(trigger?.$_ptooltipId && document.getElementById(trigger.$_ptooltipId))

  // The directive also attaches its own one-shot `mouseleave` listener directly
  // to the *generated popup element* (not the trigger) that unconditionally
  // hides it the first time the pointer leaves — with no notion of "the trigger
  // is still focused." Because that listener is created after this composable's
  // `onFocus` runs, a same-target listener can't reliably preempt it by
  // registration order; a capturing-phase listener on `document` always runs
  // before a target-phase listener on a descendant, so it's the only ordering-
  // independent way to stop it while focus is still active.
  const onPopupMouseLeave = (event: Event): void => {
    const popup = (event.target as Element | null)?.closest('[role="tooltip"]')
    if (popup?.id && popup.id === focusedTrigger?.$_ptooltipId) event.stopImmediatePropagation()
  }

  const onMouseEnter = (event: Event): void => {
    if (isSynthetic(event)) return
    isHovering.value = true
    // Our listener runs before the directive's own native mouseenter listener
    // (both are bound directly to this element), so stopping it here keeps the
    // directive from calling show() a second time — focus already opened the
    // tooltip via a synthetic mouseenter, and a second show() call creates a
    // second tooltip node that the directive's ID-tracked removal can strand —
    // but only when the tooltip focus opened is still actually showing; Escape
    // can have removed it while isFocused stayed true, and a real hover then
    // needs the native listener to run and reopen it.
    if (isFocused.value && isTooltipVisible(event.currentTarget as TooltipTrigger)) event.stopImmediatePropagation()
  }
  const onMouseLeave = (event: Event): void => {
    if (isSynthetic(event)) return
    isHovering.value = false
    // Our listener runs before the directive's own native mouseleave listener
    // (both are bound directly to this element), so stopping it here keeps the
    // tooltip open — already shown by focus — instead of letting the directive
    // hide it and then trying to undo that with a second, racy show() call.
    if (isFocused.value) event.stopImmediatePropagation()
  }
  const onFocus = (event: Event): void => {
    isFocused.value = true
    focusedTrigger = event.currentTarget as TooltipTrigger | null
    if (!isHovering.value) dispatchTooltipHoverEvent(event, 'mouseenter')
    document.addEventListener('mouseleave', onPopupMouseLeave, true)
  }
  const onBlur = (event: Event): void => {
    isFocused.value = false
    document.removeEventListener('mouseleave', onPopupMouseLeave, true)
    focusedTrigger = null
    if (!isHovering.value) dispatchTooltipHoverEvent(event, 'mouseleave')
  }

  // PrimeVue's Tooltip directive (without the `.focus` modifier) also binds a
  // native `click` listener that unconditionally hides the tooltip — a click
  // reaching the trigger while hover or focus is still active (e.g. a mouse
  // click, which fires focus and click on the same element in sequence) would
  // otherwise hide the tooltip with no future event to re-show it, since our
  // own onMouseEnter/onFocus only re-show on a *new* hover/focus transition.
  // Our listener runs before the directive's own native click listener (both
  // are bound directly to this element), so stopping it here keeps the
  // tooltip open for as long as hover or focus persists.
  const onClick = (event: Event): void => {
    if (isHovering.value || isFocused.value) event.stopImmediatePropagation()
  }

  // A `v-if`-removed trigger doesn't reliably fire blur, so a caller whose
  // trigger element can be swapped out (e.g. behind a `v-if`) must reset this
  // state itself when that happens — otherwise a stale isFocused survives
  // onto the next trigger and can suppress its real mouseleave.
  const reset = (): void => {
    isHovering.value = false
    isFocused.value = false
    document.removeEventListener('mouseleave', onPopupMouseLeave, true)
    focusedTrigger = null
  }

  return { onMouseEnter, onMouseLeave, onFocus, onBlur, onClick, reset }
}
